import { inject, Injectable } from "@angular/core";
import { FirebaseApp } from "@angular/fire/app";
import { AngularFireAuth } from "@angular/fire/compat/auth";
import {
  collection,
  doc,
  Firestore,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  where,
} from "@angular/fire/firestore";
import { Router } from "@angular/router";
import { getMessaging, getToken, Messaging, onMessage } from "firebase/messaging";
import { BehaviorSubject } from "rxjs";
import { environment } from "src/environments/environment";
import { RootStore } from "../store/root.store";
import { UserService } from "./user.service";

@Injectable({
  providedIn: "root",
})
export class NotificationService {
  currentMessage = new BehaviorSubject<any>(null);
  private firestore: Firestore;
  private afMessaging: Messaging;
  private afAuth: AngularFireAuth;
  private rootStore: RootStore;
  private router: Router;
  private userService: UserService;

  private notificationsSubject = new BehaviorSubject<any[]>([]);
  notifications$ = this.notificationsSubject.asObservable();
  switchingOrganization = false;

  constructor() {
    this.firestore = inject(Firestore);
    const firebaseApp = inject(FirebaseApp);
    this.afMessaging = getMessaging(firebaseApp);
    this.afAuth = inject(AngularFireAuth);
    this.rootStore = inject(RootStore);
    this.router = inject(Router);
    this.userService = inject(UserService);

    this.afAuth.authState.subscribe((user) => {
      if (user) {
        this.subscribeToUserNotifications(user.uid);
      } else {
        console.log("User is not authenticated");
      }
    });
  }

  async requestPermission() {
    if (!("Notification" in window) || !("serviceWorker" in navigator)) {
      console.warn("Notifications or Service Worker not supported in this browser.");
      return;
    }

    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      console.warn("Notification permission not granted.");
      return;
    }

    try {
      // ✅ Wait for the service worker to be registered and ready
      const registration = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
      await navigator.serviceWorker.ready;

      const token = await getToken(this.afMessaging, {
        vapidKey: environment.firebaseConfig.vapidKey,
        serviceWorkerRegistration: registration, // ✅ REQUIRED!
      });
      if (token) {
        localStorage.setItem("FCM_TOKEN", token);
      } else {
        console.warn("No FCM token received.");
      }

      navigator.serviceWorker.addEventListener("message", (event) => {
        if (event.data?.type === "FCM_NOTIFICATION_CLICK") {
          const { organizationId, clickAction } = event.data;

          if (organizationId === this.rootStore.userStore.userData.currentOrganization._id) {
            this.router.navigate([clickAction]);
          } else {
            this.switchingOrganization = true;
            this.userService.switchOrganization(organizationId).subscribe(() => {
              this.switchingOrganization = false;
              this.router.navigate([clickAction]);
            });
          }

          // Reset timeline state
          this.rootStore.timelineStore.activeProject = null;
          this.rootStore.timelineStore.activeUser = null;
          this.rootStore.timelineStore.freeView = false;
          this.rootStore.timelineStore.busyView = false;
          this.rootStore.timelineStore.users = [];
          this.rootStore.timelineStore.teamTabs = [];
        }
      });
    } catch (err) {
      console.error("Error getting FCM token:", err);
    }
  }

  listenForMessages() {
    onMessage(this.afMessaging, (payload) => {
      this.currentMessage.next(payload);
    });
  }

  subscribeToUserNotifications(uid: string): void {
    const notificationsRef = collection(this.firestore, `notifications/${uid}/requests`);
    const q = query(notificationsRef, orderBy("timestamp", "desc"));

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const notifications: any[] = [];
        querySnapshot.forEach((doc) => {
          notifications.push({ id: doc.id, ...doc.data() });
        });
        this.notificationsSubject.next(notifications);
      },
      (error) => {
        console.error("Error fetching notifications:", error);
      },
    );

    // return unsubscribe if you need to stop listening to updates
    // return unsubscribe;
  }

  signInWithCustomToken(customToken: string, uid: string) {
    this.afAuth
      .signInWithCustomToken(customToken)
      .then((userCredential) => {
        this.subscribeToUserNotifications(uid);
      })
      .catch((error) => {
        console.error("Error signing in with custom token:", error);
      });
  }

  markAsRead(item) {
    const notificationRef = doc(
      this.firestore,
      `notifications/${this.rootStore.userStore.userData._id}/requests/${item.id}`,
    );
    const updated = this.notificationsSubject.value.map((n) =>
      n.id === item.id ? { ...n, read: true } : n,
    );
    this.notificationsSubject.next(updated);
    return updateDoc(notificationRef, { read: true });
  }

  async markAsReadByDBId(DBid: string) {
    const notificationsRef = collection(this.firestore, `notifications/${this.rootStore.userStore.userData._id}/requests`);
    const q = query(notificationsRef, where('requestDBId', '==', DBid));
    const querySnapshot = await getDocs(q);

    querySnapshot.forEach(async (docSnap) => {
      const docRef = doc(this.firestore, docSnap.ref.path);
      await updateDoc(docRef, { read: true });
    });
  }
}
